import type { ReactNode } from "react";
import { Tag, Tooltip, Typography } from "antd";
import { entriesOf, type DsmScopeItem } from "design-scope";
import { ClassificationLabel } from "../components/scope-ui";

export type ScopeContext =
  | {
      kind: "bid";
      bidPackageId: string;
      label: string;
      /** Design package this bid package is linked under (planner lane). */
      designPackageName?: string;
      /** Design package id this bid package is planned against. */
      designPackageId?: string;
      /** Milestone id the bid package is linked to. */
      milestoneId?: string;
      milestoneName?: string;
    }
  | {
      kind: "milestone";
      designPackageId: string;
      milestoneId: string;
      /** Combined label kept for callers that do not split the heading. */
      label: string;
      designPackageTitle?: string;
      milestoneName?: string;
      committedDate?: string | null;
      projectedDate?: string | null;
      actualDate?: string | null;
      linkedBidCount?: number;
    };

/** Count pill for a scope-item context. Renders nothing when there are none. */
export function ScopeBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return <Tag color="blue">{count}</Tag>;
}

/**
 * Wraps `children` in a tooltip previewing a context's scope items: a count
 * header, the first three classifications, and a "+K more" line. Enough to
 * recognize what's there without opening the modal.
 */
export function ScopeContextTooltip({
  items,
  children
}: {
  items: DsmScopeItem[];
  children: ReactNode;
}) {
  const preview = items.slice(0, 3);
  const more = items.length - preview.length;
  const title = (
    <div className="flex flex-col gap-1">
      <Typography.Text strong>
        {items.length} scope item{items.length === 1 ? "" : "s"}
      </Typography.Text>
      {preview.map((it) => {
        const primary = entriesOf(it.classification)[0];
        return (
          <ClassificationLabel
            key={it.id}
            system={primary?.system}
            code={primary?.code}
            title={primary?.title}
          />
        );
      })}
      {more > 0 && (
        <Typography.Text type="secondary">+{more} more</Typography.Text>
      )}
    </div>
  );
  return <Tooltip title={title}>{children}</Tooltip>;
}

/** Index scope items by every bid package they are associated with. */
export function groupScopeItemsByBid(
  items: DsmScopeItem[]
): Record<string, DsmScopeItem[]> {
  const map: Record<string, DsmScopeItem[]> = {};
  items.forEach((item) => {
    item.bids.forEach((b) => {
      (map[b.bidPackageId] ??= []).push(item);
    });
  });
  return map;
}
